<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const emit = defineEmits(['error']);

// Cache global de URLs já carregadas com sucesso
const loadedUrlCache = new Set();

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const hasError = ref(false);
const showGallery = ref(false);
const isDownloading = ref(false);
const isLoading = ref(true);
const displayUrl = ref(null); // URL que está sendo exibida no img
const retryCount = ref(0);
const maxRetries = 5;
let currentPreloader = null; // Preloader Image object
let retryTimeoutId = null;

// Gera uma chave de cache baseada no attachment ID
const getCacheKey = url => {
  const attachmentId = attachment.value?.id;
  if (attachmentId) return `attachment-${attachmentId}`;
  if (url) return `url-${url.split('/').pop()?.substring(0, 30)}`;
  return null;
};

// Preload image in memory before displaying
const preloadImage = url => {
  // Cancel any previous preload
  if (currentPreloader) {
    currentPreloader.onload = null;
    currentPreloader.onerror = null;
    currentPreloader = null;
  }

  if (!url) return;

  const cacheKey = getCacheKey(url);

  // Se já carregou com sucesso antes, usar direto
  if (cacheKey && loadedUrlCache.has(cacheKey)) {
    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    return;
  }

  isLoading.value = true;
  hasError.value = false;

  // Create preloader
  const preloader = new Image();
  currentPreloader = preloader;

  preloader.onload = () => {
    if (currentPreloader !== preloader) return; // Stale preloader

    // Cache successful load
    if (cacheKey) {
      loadedUrlCache.add(cacheKey);
    }

    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    retryCount.value = 0;
  };

  preloader.onerror = () => {
    if (currentPreloader !== preloader) return; // Stale preloader

    if (retryCount.value < maxRetries) {
      retryCount.value += 1;
      const delay = 500 * retryCount.value;

      // Clear previous timeout
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }

      retryTimeoutId = setTimeout(() => {
        const currentUrl = attachment.value?.dataUrl;
        if (currentUrl) {
          preloadImage(currentUrl);
        }
      }, delay);
    } else {
      isLoading.value = false;
      hasError.value = true;
      emit('error');
    }
  };

  preloader.src = url;
};

// Watch for URL changes
watch(
  () => attachment.value?.dataUrl,
  newUrl => {
    if (newUrl) {
      retryCount.value = 0;
      preloadImage(newUrl);
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Se não tem displayUrl mas tem attachment, iniciar preload
  if (!displayUrl.value && attachment.value?.dataUrl) {
    preloadImage(attachment.value.dataUrl);
  }
});

onUnmounted(() => {
  // Cleanup
  if (currentPreloader) {
    currentPreloader.onload = null;
    currentPreloader.onerror = null;
    currentPreloader = null;
  }
  if (retryTimeoutId) {
    clearTimeout(retryTimeoutId);
  }
});

const handleLoad = () => {
  isLoading.value = false;
  hasError.value = false;
};

const handleError = () => {
  // O preloader já cuida dos retries, aqui só registramos caso o img visível dê erro
  // (não deveria acontecer porque só mostramos após preload sucesso)
  if (!isLoading.value && displayUrl.value) {
    // Tentar recarregar via preloader
    retryCount.value = 0;
    preloadImage(attachment.value?.dataUrl);
  }
};

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <!-- Loading placeholder -->
      <div
        v-if="isLoading && !displayUrl"
        class="flex items-center justify-center bg-n-alpha-2 rounded-lg"
        :style="{
          width: attachment.width
            ? `${Math.min(attachment.width, 300)}px`
            : '200px',
          height: attachment.height
            ? `${Math.min(attachment.height, 300)}px`
            : '150px',
        }"
      >
        <Icon
          icon="i-lucide-loader-2"
          class="text-n-slate-11 animate-spin size-6"
        />
      </div>
      <!-- Image (only show when we have a URL to display) -->
      <img
        v-show="displayUrl"
        class="skip-context-menu"
        :src="displayUrl"
        :width="attachment.width"
        :height="attachment.height"
        @click="onClick"
        @load="handleLoad"
        @error="handleError"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
